import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useResponsive } from '../../utils/responsive';
import CategoryDonutChart from '../statistics/CategoryDonutChart';
import TimeSeriesByCategory from '../statistics/TimeSeriesByCategory';
import StateChoroplethMap from '../statistics/StateChoroplethMap';
import AdvancedChartOptions from './AdvancedChartOptions';
import DataExporter from './DataExporter';
import PerformanceDashboard from './PerformanceDashboard';

const ComprehensiveDashboard = ({ open, onClose }) => {
  const { isMobile } = useResponsive();
  const [activeTab, setActiveTab] = useState(0);
  const [showPerformance, setShowPerformance] = useState(false);
  const [chartOptions, setChartOptions] = useState({});

  const tabs = [
    { label: 'Analytics', icon: <AnalyticsIcon /> },
    { label: 'Settings', icon: <SettingsIcon /> },
    { label: 'Export', icon: <DownloadIcon /> }
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderAnalytics = () => (
    <Grid container spacing={3}>
      {/* Charts with advanced options */}
      <Grid item xs={12} lg={6}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <AdvancedChartOptions
            chartType="donut"
            onOptionsChange={setChartOptions}
            onRefresh={() => window.location.reload()}
            showExportOptions={false}
            showFullscreenOption={false}
          />
          <CategoryDonutChart options={chartOptions} />
        </Paper>
      </Grid>

      <Grid item xs={12} lg={6}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <Typography variant="h6" gutterBottom>
            Performance Metrics
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowPerformance(true)}
            fullWidth
          >
            View Detailed Performance
          </Button>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <AdvancedChartOptions
            chartType="line"
            onOptionsChange={setChartOptions}
            onRefresh={() => window.location.reload()}
            showExportOptions={false}
            showFullscreenOption={false}
          />
          <TimeSeriesByCategory options={chartOptions} />
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <StateChoroplethMap options={chartOptions} />
        </Paper>
      </Grid>
    </Grid>
  );

  const renderSettings = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Dashboard Settings
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Performance settings and configuration options are available here.
      </Typography>
      <Button
        variant="contained"
        onClick={() => setShowPerformance(true)}
        sx={{ mt: 2 }}
      >
        View Performance Metrics
      </Button>
    </Box>
  );

  const renderExport = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Export Data
      </Typography>
      <DataExporter
        data={[]} // This would be populated with actual data
        filename="comprehensive-dashboard-export"
        title="Dashboard Data Export"
        formats={['csv', 'excel', 'json', 'pdf']}
      />
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DashboardIcon />
              <Typography variant="h6">
                Comprehensive Dashboard
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Container maxWidth={false} sx={{ p: 0 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? "fullWidth" : "standard"}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  label={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>

            {activeTab === 0 && renderAnalytics()}
            {activeTab === 1 && renderSettings()}
            {activeTab === 2 && renderExport()}
          </Container>
        </DialogContent>
      </Dialog>

      {/* Performance Dashboard */}
      <Dialog
        open={showPerformance}
        onClose={() => setShowPerformance(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Performance Dashboard
          <IconButton
            onClick={() => setShowPerformance(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <PerformanceDashboard />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ComprehensiveDashboard;
